import React, { useState, useRef } from 'react';
import { Card, Button, Upload, message, Space, Typography, Divider, Tag, Progress } from 'antd';
import { ScanOutlined, UploadOutlined, FileTextOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

interface ScannedData {
  barcode: string;
  type: string;
  timestamp: string;
  source: 'scanner' | 'file';
  rawData?: any;
}

const BarcodeScanner: React.FC = () => {
  const [scanning, setScanning] = useState(false);
  const [scannedData, setScannedData] = useState<ScannedData | null>(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [mappedData, setMappedData] = useState<any>(null);
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const startScanning = async () => {
    setScanning(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: 640, height: 480 }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      // Use BarcodeDetector API if available
      if ('BarcodeDetector' in window) {
        const detector = new (window as any).BarcodeDetector({
          formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e', 'qr_code']
        });
        const interval = setInterval(async () => {
          if (videoRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
            try {
              const barcodes = await detector.detect(videoRef.current);
              if (barcodes.length > 0) {
                clearInterval(interval);
                handleBarcodeDetected(barcodes[0].rawValue, 'scanner');
                stopScanning();
              }
            } catch (e) {
              // Detection frame error, continue scanning
            }
          }
        }, 500);
        // Store interval for cleanup
        (videoRef.current as any)._scanInterval = interval;
      } else {
        message.info('Barcode-Erkennung wird in diesem Browser nicht nativ unterstützt. Bitte nutzen Sie den Datei-Import.');
      }
    } catch (err) {
      console.error('Camera error:', err);
      message.error('Kamera konnte nicht gestartet werden');
      setScanning(false);
    }
  };

  const stopScanning = () => {
    if (videoRef.current) {
      const stream = videoRef.current.srcObject as MediaStream;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      videoRef.current.srcObject = null;
      if ((videoRef.current as any)._scanInterval) {
        clearInterval((videoRef.current as any)._scanInterval);
      }
    }
    setScanning(false);
  };

  const handleBarcodeDetected = (barcode: string, source: 'scanner' | 'file') => {
    const data: ScannedData = {
      barcode,
      type: detectBarcodeType(barcode),
      timestamp: new Date().toISOString(),
      source
    };
    
    setScannedData(data);
    message.success(`Barcode erkannt: ${barcode}`);
    
    // Automatically start data mapping
    mapBarcodeToDPPData(barcode);
  };

  const detectBarcodeType = (barcode: string): string => {
    if (barcode.length === 13) return 'EAN13';
    if (barcode.length === 8) return 'EAN8';
    if (barcode.length === 12) return 'UPC';
    if (barcode.match(/^[A-Z0-9]{17}$/)) return 'VIN';
    return 'CODE128';
  };

  const mapBarcodeToDPPData = async (barcode: string) => {
    setProcessing(true);
    setProgress(0);

    try {
      // Simulate data mapping process
      const steps = [
        'Barcode validieren...',
        'Produktdaten abrufen...',
        'Dynamics 365 abfragen...',
        'Materialien mappen...',
        'Compliance-Check...',
        'DPP-Struktur erstellen...'
      ];

      for (let i = 0; i < steps.length; i++) {
        setProgress((i + 1) * (100 / steps.length));
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Mock mapped data (in real app, this would come from your backend)
      const mockMappedData = {
        productIdentification: {
          gtin: barcode,
          oemNumber: `OEM-${barcode.slice(-6)}`,
          productName: 'Automotive Component Module',
          manufacturer: 'ABC Zulieferer GmbH'
        },
        materials: {
          composition: [
            { material: 'Steel', weight_percent: 45 },
            { material: 'Plastic', weight_percent: 25 },
            { material: 'Aluminum', weight_percent: 20 },
            { material: 'Electronics', weight_percent: 10 }
          ],
          recyclingContent: 28,
          hazardousSubstances: 'RoHS compliant'
        },
        sustainability: {
          co2Footprint: 14.2,
          energyClass: 'A++',
          waterConsumption: 180,
          certifications: ['ISO 9001', 'ISO 14001']
        },
        supplyChain: {
          originCountry: 'DE',
          supplier: 'ABC Zulieferer GmbH',
          transportConditions: {
            temperatureMin: 15,
            temperatureMax: 25,
            humidityMin: 40,
            humidityMax: 60
          }
        },
        compliance: {
          ceMarked: true,
          reachCompliant: true,
          rohsCompliant: true,
          localRegulations: ['Germany: ProdSG', 'EU: Regulation 1907/2006']
        },
        missingFields: [
          'serialNumber',
          'batchNumber',
          'manufacturingDate'
        ]
      };

      setMappedData(mockMappedData);
      message.success('Daten erfolgreich gemappt!');

    } catch (error) {
      message.error('Fehler beim Daten-Mapping');
      console.error('Mapping error:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    const fileType = file.type;
    
    if (fileType === 'text/csv') {
      processCSVFile(file);
    } else if (fileType === 'application/json') {
      processJSONFile(file);
    } else if (fileType === 'text/xml' || fileType === 'application/xml') {
      processXMLFile(file);
    } else if (file.type.startsWith('image/')) {
      processImageFile(file);
    } else {
      message.error('Nicht unterstütztes Dateiformat');
    }
  };

  const processCSVFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');
      const headers = lines[0].split(',');
      
      // Find barcode column
      const barcodeColumnIndex = headers.findIndex(h => 
        h.toLowerCase().includes('barcode') || 
        h.toLowerCase().includes('gtin') || 
        h.toLowerCase().includes('ean')
      );
      
      if (barcodeColumnIndex !== -1 && lines.length > 1) {
        const firstBarcode = lines[1].split(',')[barcodeColumnIndex];
        handleBarcodeDetected(firstBarcode.trim(), 'file');
      }
    };
    reader.readAsText(file);
  };

  const processJSONFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target?.result as string);
        const barcode = json.barcode || json.gtin || json.product_code;
        if (barcode) {
          handleBarcodeDetected(barcode, 'file');
          setMappedData(json);
        }
      } catch (error) {
        message.error('Ungültiges JSON-Format');
      }
    };
    reader.readAsText(file);
  };

  const processXMLFile = (file: File) => {
    // XML processing implementation
    message.info('XML-Verarbeitung wird implementiert');
  };

  const processImageFile = async (file: File) => {
    try {
      if ('BarcodeDetector' in window) {
        const bitmap = await createImageBitmap(file);
        const detector = new (window as any).BarcodeDetector({
          formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e', 'qr_code']
        });
        const barcodes = await detector.detect(bitmap);
        if (barcodes.length > 0) {
          handleBarcodeDetected(barcodes[0].rawValue, 'file');
        } else {
          message.error('Kein Barcode im Bild gefunden');
        }
      } else {
        message.error('Barcode-Erkennung aus Bildern wird in diesem Browser nicht unterstützt');
      }
    } catch (error) {
      message.error('Kein Barcode im Bild gefunden');
    }
  };

  const proceedToValidation = () => {
    if (mappedData) {
      navigate('/data-validation', { state: { mappedData, scannedData } });
    }
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px' }}>
      <Card title="Barcode / QR-Code Scanner" bordered={false}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          
          {/* Scanner Section */}
          <div>
            <Title level={4}>1. Barcode scannen oder importieren</Title>
            <Space size="large">
              <Button 
                type="primary" 
                icon={<ScanOutlined />}
                onClick={scanning ? stopScanning : startScanning}
                size="large"
              >
                {scanning ? 'Scannen stoppen' : 'Kamera-Scanner starten'}
              </Button>
              
              <Upload
                accept=".csv,.json,.xml,image/*"
                beforeUpload={(file) => {
                  handleFileUpload(file);
                  return false;
                }}
                showUploadList={false}
              >
                <Button icon={<UploadOutlined />} size="large">
                  Datei importieren
                </Button>
              </Upload>
            </Space>
          </div>

          {/* Video Preview */}
          {scanning && (
            <div style={{ textAlign: 'center' }}>
              <video
                ref={videoRef}
                style={{ width: '100%', maxWidth: 640, height: 480 }}
                autoPlay
                playsInline
              />
            </div>
          )}

          {/* Scanned Data Display */}
          {scannedData && (
            <Card size="small" title="Erkannter Barcode">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>Barcode: </Text>
                  <Tag color="blue">{scannedData.barcode}</Tag>
                </div>
                <div>
                  <Text strong>Typ: </Text>
                  <Tag>{scannedData.type}</Tag>
                </div>
                <div>
                  <Text strong>Quelle: </Text>
                  <Tag icon={scannedData.source === 'scanner' ? <ScanOutlined /> : <FileTextOutlined />}>
                    {scannedData.source === 'scanner' ? 'Kamera-Scan' : 'Datei-Import'}
                  </Tag>
                </div>
                <div>
                  <Text strong>Zeitpunkt: </Text>
                  {new Date(scannedData.timestamp).toLocaleString('de-DE')}
                </div>
              </Space>
            </Card>
          )}

          {/* Processing Progress */}
          {processing && (
            <Card size="small" title="Daten werden verarbeitet...">
              <Progress percent={progress} status="active" />
            </Card>
          )}

          {/* Mapped Data Display */}
          {mappedData && (
            <Card 
              size="small" 
              title={
                <Space>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  Gemappte DPP-Daten
                </Space>
              }
              extra={
                <Button type="primary" onClick={proceedToValidation}>
                  Zur Validierung
                </Button>
              }
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>Produkt: </Text>
                  {mappedData.productIdentification.productName}
                </div>
                <div>
                  <Text strong>Hersteller: </Text>
                  {mappedData.productIdentification.manufacturer}
                </div>
                <div>
                  <Text strong>Materialien: </Text>
                  {mappedData.materials.composition.map((m: any) => m.material).join(', ')}
                </div>
                <div>
                  <Text strong>CO₂-Fußabdruck: </Text>
                  {mappedData.sustainability.co2Footprint} kg CO₂-eq
                </div>
                
                {mappedData.missingFields && mappedData.missingFields.length > 0 && (
                  <div>
                    <Text strong style={{ color: '#fa8c16' }}>Fehlende Daten: </Text>
                    <Space wrap>
                      {mappedData.missingFields.map((field: string) => (
                        <Tag key={field} color="orange">{field}</Tag>
                      ))}
                    </Space>
                  </div>
                )}
              </Space>
            </Card>
          )}

          <Divider />
          
          <div style={{ textAlign: 'center' }}>
            <Text type="secondary">
              Unterstützte Formate: EAN13, EAN8, UPC, Code128, Code39, CSV, JSON, XML, Bilder
            </Text>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default BarcodeScanner;
